/**
 * The boot probe: where the governor starts.
 *
 * `core/perf.ts` closes the loop while the game runs — it measures presented
 * frames and walks a ladder of concessions when one costs more than the display
 * can take. It is good at that and blind to one thing: it starts wherever it was
 * handed, so a machine that cannot afford `ultra` spends its first half-minute
 * stuttering its way down to the tier it could have opened at. This picks the
 * opening. It is not a second governor and owns no per-frame code.
 *
 * Four rules shape it.
 *
 *  1. **Measure the real chain, never a proxy.** A synthetic shader would be a
 *     guess about the cost of the CRT composite, the bloom pyramid, the halation
 *     blur and the persistence feedback. Instead the probe renders honest frames
 *     through the assembled renderer, once per candidate tier, at the viewport
 *     the player actually has — so the dpr cap that separates the three tiers is
 *     measured rather than modelled. Cost is read off the wall clock with a
 *     `readPixels` stall, which is the one sync every driver honours; the
 *     governor's timer query needs several frames to come back and the probe has
 *     no frames to spare.
 *  2. **Short, and honest about it.** Roughly 40ms of timed work, hard-capped at
 *     {@link TIMED_BUDGET_MS}, plus the shader compilation the first real frame
 *     would have paid anyway. It stops at the first tier that fits, so the
 *     machines that need the probe least pay for it least.
 *  3. **Never above an explicit choice.** The probe supplies a *starting* tier
 *     and a *starting* rung. A tier the player picked in Settings stays a
 *     ceiling: {@link rememberChoice} records it, and {@link storedVerdict}
 *     hands it back on the next load in place of whatever the probe measured.
 *  4. **Never fatal.** Every entry point catches and returns `null`. A machine
 *     the probe cannot measure gets today's behaviour — `ultra`, rung 0, the
 *     governor working it out from there — and nothing on screen says so.
 */

import { QUALITY } from './config';
import type { QualityTier } from './config';

/** Most expensive first. The probe walks it down and stops at the first fit. */
const TIERS: readonly QualityTier[] = ['ultra', 'standard', 'potato'];

/**
 * Fraction of the frame budget a *quiet* frame may spend and still earn a tier.
 *
 * Quiet is the whole difficulty: an empty board is not what the tier has to
 * survive. Measured on this project with `tools/perfprobe.mjs --auto=off` at
 * 1920x1080, ultra, governor off: the `empty` scenario costs 5.3ms at the median
 * and the 246-body `swarm` costs 7.0ms, with the p95 going 13.9 -> 28.6. So the
 * board's own share of a hard frame is worth a little over half of a quiet one,
 * and the tail is worth twice it.
 *
 * 0.5 of a 14.9ms budget is therefore ~7.5ms of quiet frame, which leaves the
 * same again for the game on top. It is deliberately not tighter: that machine
 * spends 35% of its budget on a quiet frame and is unambiguously an ultra
 * machine, and the p95 it cannot hold under a 3x swarm is exactly the case the
 * ladder exists to walk down. The probe's job is to catch the machine that
 * cannot afford the *empty* frame, and to hand everything subtler to the loop.
 */
const QUIET_FRACTION = 0.5;

/**
 * How far over its allowance a tier may sit and still be kept, with rungs.
 *
 * The tiers are coarse and the ladder is not: `potato` gives up persistence,
 * halation and half the bloom pyramid in one step, while the ladder's first four
 * rungs drop the generative background and thin the particles — which is the
 * order `perf.ts` argues for at length, cheapest visual cost first. So a tier
 * four percent over its allowance keeps its tier and opens a few rungs down
 * rather than losing the CRT look outright. Measured under an 8x CPU throttle:
 * `ultra` at 7.8ms against 7.5ms allowed, where dropping to `potato` bought 3.6ms
 * by switching off the two passes the cabinet is *for*.
 *
 * Past 1.35 the tier goes, because beyond there the tier's dpr cap — the one
 * lever the ladder only reaches at its very last rung — is the whole difference.
 */
const TIER_SLACK = 1.35;

/**
 * Starting rung by how much of that allowance the chosen tier already spends.
 *
 * Read as: a machine sitting on 90% of what its tier is allowed opens two rungs
 * down rather than discovering the same thing over the first thirty seconds. The
 * numbers past 1.0 only occur when even `potato` overruns, where the ladder is
 * all that is left. Being wrong here is cheap in one direction only, and it is
 * this one: the governor restores a rung it did not need after four seconds of
 * quiet, and a rung it needed and did not take is a visible stutter.
 */
const RUNG_STEPS: readonly { under: number; rung: number }[] = [
  { under: 0.60, rung: 0 },
  { under: 0.85, rung: 1 },
  { under: 1.00, rung: 2 },
  { under: 1.50, rung: 4 },
  { under: 2.50, rung: 6 },
  { under: Infinity, rung: 8 },
];

/** Timed frames per tier. Four cheap looks, of which the best one counts. */
const TIMED_FRAMES = 4;
/**
 * Hard cap on *timed* work, in ms.
 *
 * Warmup is excluded on purpose: those frames compile the composite, the
 * pyramid, the halation blur and every scene material, and the first real frame
 * pays that bill whether the probe exists or not. What the probe adds is the
 * timed frames, and 60ms of them is under four frames of the run it is about to
 * start. A benchmark long enough to notice is worse than no benchmark.
 */
export const TIMED_BUDGET_MS = 60;
/**
 * Hard cap on everything the probe adds, in ms, with the first frame's shader
 * compilation subtracted — that frame is the boot's, not the probe's.
 *
 * The timed budget alone is not enough of a leash. A software rasteriser spends
 * 35ms on a frame it will not be asked to draw again, and three of those plus
 * warmups walked past 900ms in testing before this existed.
 */
const WALL_BUDGET_MS = 240;

/** Storage key prefix. The device signature is appended, so a new GPU misses. */
const KEY_PREFIX = 'pixeltd.bench.v2';

/**
 * How far the viewport may move before a stored verdict is thrown away.
 *
 * The composite runs at output resolution, so the numbers in the store belong to
 * the window they were taken in. Reloading into a window 60% larger means the
 * stored cost understates the new one by about that much, which is enough to
 * pick the wrong tier — and re-measuring costs 60ms.
 */
const PIXEL_TOLERANCE = 1.6;

/** What the probe measured, and enough context to know when it went stale. */
export interface BenchSample {
  /** Median quiet-frame cost per tier, in ms. Absent where the probe stopped. */
  quietMs: Partial<Record<QualityTier, number>>;
  /** Output pixels each measured tier was drawing, for the staleness check. */
  pixels: Partial<Record<QualityTier, number>>;
  /** Half-float colour attachments exist. Without them the tier is `potato`. */
  hdr: boolean;
  /** Unmasked GL renderer string, or '' when the driver hides it. */
  gpu: string;
  /** Wall time the whole probe took, warmup included. */
  elapsedMs: number;
  /** The first frame ever rendered, which is the shader compile boot owes anyway. */
  compileMs: number;
  /** False when a candidate went unmeasured because a budget ran out. */
  complete: boolean;
}

export interface BenchVerdict {
  tier: QualityTier;
  /** Ladder rung the governor opens on. 0 is exactly the tier. */
  rung: number;
  /** Measured quiet-frame cost of the chosen tier, ms. 0 when unmeasured. */
  quietMs: number;
  /** What a quiet frame was allowed to cost. */
  allowanceMs: number;
  /** One line, for the settings readout and the boot toast. */
  reason: string;
  /** True when this came off a fresh measurement rather than out of the store. */
  fresh: boolean;
}

/** What `main.ts` drives the probe through. Kept narrow so tests can fake it. */
export interface BenchHost {
  /** One frame through the real chain, exactly as the loop renders it. */
  frame(): void;
  /** Returns once the GPU has finished everything submitted so far. */
  sync(): void;
  /** Applies a candidate tier — every knob the tier owns, not just the post stack. */
  setTier(tier: QualityTier): void;
  /** Output pixels the current tier draws. */
  pixels(): number;
  /** Half-float colour attachments exist. */
  readonly hdr: boolean;
  /** Unmasked GL renderer, for the store signature. */
  readonly gpu: string;
}

/** The last verdict this page arrived at, however it arrived at it. */
let last: BenchVerdict | null = null;
/** The last measurement taken in this page, or null when none was needed. */
let lastMeasured: BenchSample | null = null;

export function lastVerdict(): BenchVerdict | null {
  return last;
}

export function lastSample(): BenchSample | null {
  return lastMeasured;
}

function now(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

/**
 * Renders `count` frames and returns the median cost of one, in ms.
 *
 * Each frame is followed by a stall, so the number includes the GPU rather than
 * just the submission — the whole point, since every tier the probe is choosing
 * between differs in GPU work and barely at all in CPU work.
 */
/**
 * Cost of the cheapest of `count` frames, in ms.
 *
 * The *minimum*, not the median, and this is the difference between a verdict
 * and a coin toss. Boot is the noisiest moment in the whole session — the fonts
 * are landing, the HUD is being laid out, the GPU is still in a low power state,
 * and every one of those makes a frame look *more* expensive than the machine
 * is. Measured with the median of three: the same M4 Max reported 1.3, 1.4, 1.6
 * and 6.5ms across four boots, which is a tier and two rungs of disagreement
 * about one machine. The minimum answers the question the probe is actually
 * asking — what can this machine do when nothing is in its way — and leaves the
 * question of what it *sustains* to the governor, which has a thousand frames to
 * work with instead of four.
 */
function bestFrame(host: BenchHost, count: number, ceilingMs: number): number {
  let best = Infinity;
  for (let i = 0; i < count; i++) {
    const t0 = now();
    host.frame();
    host.sync();
    const ms = now() - t0;
    if (ms < best) best = ms;
    // Two frames, both at twice what the tier is allowed, have answered the
    // question — and on the machines where that happens each further look is the
    // most expensive thing the probe does. Two rather than one because a single
    // slow frame is the boot noise this whole function exists to see past.
    if (best > ceilingMs && i >= 1) break;
  }
  return best;
}

/**
 * Measures the assembled renderer, tier by tier, most expensive first.
 *
 * Stops at the first tier that fits: on a machine that can hold `ultra` there is
 * nothing to learn from `potato` and the probe would be spending the player's
 * boot to learn it. When a tier overruns and the budget is thin, the walk skips
 * to the *cheapest* tier rather than the next one down — the machine that ran the
 * budget out is exactly the machine whose floor needs measuring, and an early
 * version of this file spent its whole budget on `ultra`, measured nothing else,
 * and then reported `ultra` for a software rasteriser drawing at 34.8ms a frame.
 */
export function measure(
  host: BenchHost,
  allowanceMs: number,
  budgetMs: number = TIMED_BUDGET_MS,
  wallMs: number = WALL_BUDGET_MS,
): BenchSample {
  const quietMs: Partial<Record<QualityTier, number>> = {};
  const pixels: Partial<Record<QualityTier, number>> = {};
  const start = now();
  let timed = 0;
  let compileMs = 0;
  let complete = true;

  for (let i = 0; i < TIERS.length; i++) {
    const tier = TIERS[i];
    const cheapest = i === TIERS.length - 1;
    // The compile is boot's bill, not the probe's, so it is measured and then
    // excluded from the leash rather than hidden inside it.
    const added = now() - start - compileMs;
    if ((timed >= budgetMs || added >= wallMs) && !cheapest) {
      complete = false;
      continue;
    }
    if (added >= wallMs * 2) {
      complete = false;
      break;
    }

    host.setTier(tier);
    const warm0 = now();
    host.frame();
    host.sync();
    const warm = now() - warm0;
    if (i === 0) compileMs = warm;
    // A second warm frame only where one was cheap. On a fast machine it settles
    // the clocks; on a slow one it is 35ms spent confirming what the first said.
    if (warm < allowanceMs) {
      host.frame();
      host.sync();
    }

    const t0 = now();
    const ms = bestFrame(host, TIMED_FRAMES, allowanceMs * 2);
    timed += now() - t0;

    quietMs[tier] = ms;
    pixels[tier] = host.pixels();
    if (host.hdr && fits(ms, allowanceMs)) break;
  }

  lastMeasured = {
    quietMs,
    pixels,
    hdr: host.hdr,
    gpu: host.gpu,
    elapsedMs: now() - start,
    compileMs,
    complete,
  };
  return lastMeasured;
}

/**
 * Turns measurements into a tier and an opening rung.
 *
 * `budgetMs` is the governor's own budget — target minus the headroom the
 * compositor and the OS want — so the two agree about what a frame costs before
 * either of them acts on it.
 */
export function verdictFor(sample: BenchSample, budgetMs: number, fresh: boolean): BenchVerdict {
  const allowanceMs = budgetMs * QUIET_FRACTION;

  // No half-float colour means the bright pass has nothing above threshold to
  // work with and every glow in the game is already broken. Whatever the frame
  // times say, this is not a machine to open at ultra on.
  if (!sample.hdr) {
    const ms = sample.quietMs.potato ?? sample.quietMs.standard ?? sample.quietMs.ultra ?? 0;
    return record({
      tier: 'potato',
      rung: Math.max(1, rungFor(ms, allowanceMs)),
      quietMs: ms,
      allowanceMs,
      reason: 'no half-float render targets',
      fresh,
    });
  }

  for (const tier of TIERS) {
    const ms = sample.quietMs[tier];
    if (ms === undefined || !fits(ms, allowanceMs)) continue;
    return record({
      tier,
      rung: rungFor(ms, allowanceMs),
      quietMs: ms,
      allowanceMs,
      reason: `${ms.toFixed(1)}ms quiet frame against ${allowanceMs.toFixed(1)}ms allowed`,
      fresh,
    });
  }

  /*
   * Nothing fitted. The cheapest tier is the answer whether or not the budget
   * stretched far enough to measure it — an unmeasured `potato` is a guess, but
   * it is a guess in the one direction the governor undoes for free, and the
   * alternative is what this code did before: report `ultra` for the machine
   * that could least afford it because it had run out of time to look.
   *
   * The rung comes from the cheapest measurement there is, so a frame at five
   * times its allowance opens several rungs down rather than at the tier's top.
   */
  const floor = TIERS[TIERS.length - 1];
  let best = 0;
  for (const tier of TIERS) {
    const ms = sample.quietMs[tier];
    if (ms !== undefined && (best === 0 || ms < best)) best = ms;
  }
  if (best > 0) {
    return record({
      tier: floor,
      rung: Math.max(1, rungFor(best, allowanceMs)),
      quietMs: best,
      allowanceMs,
      reason: sample.quietMs[floor] !== undefined
        ? `${best.toFixed(1)}ms quiet frame over ${allowanceMs.toFixed(1)}ms at every tier`
        : `${best.toFixed(1)}ms quiet frame, cheapest tier unmeasured`,
      fresh,
    });
  }

  // Nothing measured at all. Today's behaviour, which is the fallback throughout.
  return record({
    tier: 'ultra',
    rung: 0,
    quietMs: 0,
    allowanceMs,
    reason: 'not measured',
    fresh,
  });
}

/** A tier is affordable when a quiet frame fits its allowance, plus the slack. */
function fits(quietMs: number, allowanceMs: number): boolean {
  return quietMs <= allowanceMs * TIER_SLACK;
}

function rungFor(quietMs: number, allowanceMs: number): number {
  if (!(quietMs > 0) || !(allowanceMs > 0)) return 0;
  const load = quietMs / allowanceMs;
  for (const step of RUNG_STEPS) if (load < step.under) return step.rung;
  return RUNG_STEPS[RUNG_STEPS.length - 1].rung;
}

function record(v: BenchVerdict): BenchVerdict {
  last = v;
  return v;
}

// ────────────────────────────────────────────────────────────── persistence ──

interface StoredBench {
  v: 2;
  sig: string;
  sample: BenchSample;
  /** A tier the player picked by hand. Outranks anything measured. */
  chosen?: QualityTier;
  at: number;
}

/**
 * What makes two boots the same machine.
 *
 * The GPU string is the whole point — a verdict carried over from a different
 * card is worse than no verdict, because it is confidently wrong instead of
 * absent. `devicePixelRatio` rides along because it decides how much the tier's
 * dpr cap is actually capping, and the half-float bit because it is the one
 * measurement that changes the answer on its own.
 */
function signature(gpu: string, hdr: boolean): string {
  const dpr = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
  const platform = typeof navigator === 'undefined' ? '' : navigator.platform || '';
  return hash(`${gpu}|${platform}|${dpr}|${hdr ? 'hdr' : 'ldr'}`);
}

/** FNV-1a, base36. Short enough to read in devtools, wide enough not to collide. */
function hash(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

function store(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Safari in a blocked third-party frame throws on the getter itself.
    return null;
  }
}

/**
 * The verdict this machine already reached, or `null` to go and measure.
 *
 * Returns `null` rather than a stale answer in four cases: no store, a different
 * device signature, a viewport that has moved too far for the numbers to mean
 * anything, and a blob this version does not recognise. A remembered explicit
 * choice survives all of that except the signature — it is the player's opinion,
 * not a measurement, and it does not go out of date when the window resizes.
 */
export function storedVerdict(gpu: string, hdr: boolean, budgetMs: number, currentPixels: number): BenchVerdict | null {
  const held = read(gpu, hdr);
  if (held === null) return null;

  const v = verdictFor(held.sample, budgetMs, false);
  const measured = held.sample.pixels[v.tier];
  if (measured !== undefined && measured > 0 && currentPixels > 0) {
    const ratio = currentPixels > measured ? currentPixels / measured : measured / currentPixels;
    if (ratio > PIXEL_TOLERANCE) return null;
  }

  if (held.chosen !== undefined && held.chosen !== v.tier) {
    // The player's tier, with the probe's rung carried across only where it
    // still makes sense: a chosen tier is a ceiling, and opening several rungs
    // under a ceiling the player set by hand is the governor's business to earn,
    // not the store's to assume.
    return record({
      tier: held.chosen,
      rung: held.chosen === 'potato' ? v.rung : 0,
      quietMs: v.quietMs,
      allowanceMs: v.allowanceMs,
      reason: 'tier chosen in settings',
      fresh: false,
    });
  }
  return v;
}

function read(gpu: string, hdr: boolean): StoredBench | null {
  const s = store();
  if (s === null) return null;
  try {
    const raw = s.getItem(`${KEY_PREFIX}.${signature(gpu, hdr)}`);
    if (raw === null) return null;
    const blob = JSON.parse(raw) as StoredBench;
    if (blob === null || blob.v !== 2 || typeof blob.sample !== 'object') return null;
    if (typeof blob.sample.quietMs !== 'object' || blob.sample.quietMs === null) return null;
    if (blob.chosen !== undefined && QUALITY[blob.chosen] === undefined) return null;
    return blob;
  } catch {
    return null;
  }
}

function write(gpu: string, hdr: boolean, next: StoredBench): void {
  const s = store();
  if (s === null) return;
  const key = `${KEY_PREFIX}.${signature(gpu, hdr)}`;
  try {
    // One machine keeps one row. An old signature is a GPU or a display this
    // browser is no longer on, and leaving it behind is a slow leak of blobs
    // nothing will ever read again.
    for (let i = s.length - 1; i >= 0; i--) {
      const k = s.key(i);
      if (k !== null && k !== key && k.startsWith(KEY_PREFIX)) s.removeItem(k);
    }
    s.setItem(key, JSON.stringify(next));
  } catch {
    // A full or disabled store is not a reason to fail a boot.
  }
}

/** Persists a fresh measurement, keeping any remembered explicit choice. */
export function saveSample(sample: BenchSample): void {
  const held = read(sample.gpu, sample.hdr);
  write(sample.gpu, sample.hdr, {
    v: 2,
    sig: signature(sample.gpu, sample.hdr),
    sample,
    chosen: held?.chosen,
    at: Date.now(),
  });
}

/**
 * Records that the player picked a tier by hand.
 *
 * This is the whole of rule 3. Without it a player who drops to `potato` on a
 * machine the probe rates `ultra` is silently overruled on the next load, and
 * the probe becomes something that argues with them once per boot.
 */
export function rememberChoice(gpu: string, hdr: boolean, tier: QualityTier): void {
  const held = read(gpu, hdr);
  if (held === null) return;
  if (held.chosen === tier) return;
  write(gpu, hdr, { ...held, chosen: tier, at: Date.now() });
}

// Debug surface for tools/benchprobe.mjs, beside `perf.ts`'s. Read-only: the
// probe runs from `main.ts` and this is only how a tool reads what it decided.
if (typeof window !== 'undefined') {
  (window as unknown as {
    __pixeltdBench?: { verdict: typeof lastVerdict; sample: typeof lastSample };
  }).__pixeltdBench = { verdict: lastVerdict, sample: lastSample };
}

/** Drops everything stored for this machine, so the next probe measures again. */
export function forget(gpu: string, hdr: boolean): void {
  const s = store();
  if (s === null) return;
  try {
    s.removeItem(`${KEY_PREFIX}.${signature(gpu, hdr)}`);
  } catch {
    // Nothing to do about a store that will not delete.
  }
}
